import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const EzCaterDetailsDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  memoDetail,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showAllOrderIds, setShowAllOrderIds] = useState(false);
  const [orderBy, setOrderBy] = useState('totalAmount');
  const [order, setOrder] = useState('desc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter data based on column filters
  const filteredDetails = useMemo(() => {
    if (!details.length) return details;

    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;

    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        const cellValue = String(row[columnId] ?? '').toLowerCase();
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric sorting for amounts
      if (['paymentAmount', 'tipAmount', 'taxAmount', 'deliveryFee', 'salesAmount', 'totalAmount'].includes(orderBy)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        // String sorting
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  // Extract delivery partner from memoDetail (e.g., "211600 - ezCater Delivery Tips - DeliverThat" -> "DeliverThat")
  const extractedDeliveryPartner = useMemo(() => {
    if (!memoDetail) return null;
    const match = memoDetail.match(/Delivery Tips\s*-\s*(.+)$/i);
    return match ? match[1].trim() : null;
  }, [memoDetail]);

  useEffect(() => {
    if (open && businessDate && storeId) {
      fetchDetails();
    }
  }, [open, businessDate, storeId, extractedDeliveryPartner]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);

      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const params = {
        businessDate,
        storeId,
      };
      // Add delivery partner filter if extracted from memoDetail
      if (extractedDeliveryPartner) {
        params.deliveryPartner = extractedDeliveryPartner;
      }

      const response = await axios.get('/hd-api/gl-export/ezcater-details', {
        params,
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
      });

      setDetails(response.data.data || []);
    } catch (err) {
      console.error('Error fetching ezCater details:', err);
      setError('Failed to load ezCater details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate totals from filtered data
  const totals = useMemo(() => {
    return filteredDetails.reduce(
      (acc, d) => ({
        sales: acc.sales + (parseFloat(d.salesAmount) || 0),
        deliveryFee: acc.deliveryFee + (parseFloat(d.deliveryFee) || 0),
        tax: acc.tax + (parseFloat(d.taxAmount) || 0),
        tip: acc.tip + (parseFloat(d.tipAmount) || 0),
        total: acc.total + (parseFloat(d.totalAmount) || 0),
      }),
      { sales: 0, deliveryFee: 0, tax: 0, tip: 0, total: 0 }
    );
  }, [filteredDetails]);

  // Count by order type from filtered data
  const orderTypeCounts = useMemo(() => {
    return filteredDetails.reduce(
      (acc, d) => {
        if (d.orderType === 'Delivery') acc.delivery++;
        else if (d.orderType === 'Pickup') acc.pickup++;
        return acc;
      },
      { delivery: 0, pickup: 0 }
    );
  }, [filteredDetails]);

  // Check if any filters are active
  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Export to Excel
  const handleExport = () => {
    const exportData = sortedDetails.map((item) => {
      const row = {
        'Order Type': item.orderType,
        'Delivery Partner': item.deliveryPartner || '',
        'State': item.state,
        'Sales': parseFloat(item.salesAmount) || 0,
        'Delivery Fee': parseFloat(item.deliveryFee) || 0,
        'Tax': parseFloat(item.taxAmount) || 0,
        'Tip': parseFloat(item.tipAmount) || 0,
        'Total': parseFloat(item.totalAmount) || 0,
      };
      // Include all order IDs if checkbox is checked
      if (showAllOrderIds) {
        row['Order GUID'] = item.orderGuid || '';
        row['Olo ID'] = item.oloRef || '';
        row['ezCater ID'] = item.externalReference || '';
      }
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ezCater Details');
    XLSX.writeFile(wb, `ezcater-details-${storeId}-${businessDate}.xlsx`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StorefrontIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            ezCater Order Details
            {extractedDeliveryPartner && (
              <Typography component="span" variant="h6" color="primary" sx={{ ml: 1 }}>
                - {extractedDeliveryPartner}
              </Typography>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            Store {storeId} - {businessDate}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            icon={<LocalShippingIcon sx={{ fontSize: 16 }} />}
            label={`${orderTypeCounts.delivery} Delivery`}
            size="small"
            color="info"
            variant="outlined"
          />
          <Chip
            icon={<StorefrontIcon sx={{ fontSize: 16 }} />}
            label={`${orderTypeCounts.pickup} Pickup`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && (
          <Alert severity="info">
            No ezCater order details found for this date.
          </Alert>
        )}

        {!loading && !error && details.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: 400, userSelect: 'text' }} onCopy={handleCopy}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {showAllOrderIds && (
                      <>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                          <TableSortLabel
                            active={orderBy === 'orderGuid'}
                            direction={orderBy === 'orderGuid' ? order : 'asc'}
                            onClick={() => handleRequestSort('orderGuid')}
                          >
                            Order GUID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                          <TableSortLabel
                            active={orderBy === 'oloRef'}
                            direction={orderBy === 'oloRef' ? order : 'asc'}
                            onClick={() => handleRequestSort('oloRef')}
                          >
                            Olo ID
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                          <TableSortLabel
                            active={orderBy === 'externalReference'}
                            direction={orderBy === 'externalReference' ? order : 'asc'}
                            onClick={() => handleRequestSort('externalReference')}
                          >
                            ezCater ID
                          </TableSortLabel>
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'orderType'}
                        direction={orderBy === 'orderType' ? order : 'asc'}
                        onClick={() => handleRequestSort('orderType')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'deliveryPartner'}
                        direction={orderBy === 'deliveryPartner' ? order : 'asc'}
                        onClick={() => handleRequestSort('deliveryPartner')}
                      >
                        Delivery Partner
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'salesAmount'}
                        direction={orderBy === 'salesAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('salesAmount')}
                      >
                        Sales
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'deliveryFee'}
                        direction={orderBy === 'deliveryFee' ? order : 'asc'}
                        onClick={() => handleRequestSort('deliveryFee')}
                      >
                        Delivery Fee
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'taxAmount'}
                        direction={orderBy === 'taxAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('taxAmount')}
                      >
                        Tax
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'tipAmount'}
                        direction={orderBy === 'tipAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('tipAmount')}
                      >
                        Tip
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'totalAmount'}
                        direction={orderBy === 'totalAmount' ? order : 'asc'}
                        onClick={() => handleRequestSort('totalAmount')}
                      >
                        Total
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                  {/* Filter Row */}
                  <TableRow>
                    {showAllOrderIds && (
                      <>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Filter..."
                            value={columnFilters['orderGuid'] || ''}
                            onChange={(e) => handleColumnFilterChange('orderGuid', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                              '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                              minWidth: 80,
                              width: '100%',
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                                </InputAdornment>
                              ),
                              endAdornment: columnFilters['orderGuid'] && (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => clearColumnFilter('orderGuid')} sx={{ p: 0.25 }}>
                                    <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Filter..."
                            value={columnFilters['oloRef'] || ''}
                            onChange={(e) => handleColumnFilterChange('oloRef', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                              '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                              minWidth: 80,
                              width: '100%',
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                                </InputAdornment>
                              ),
                              endAdornment: columnFilters['oloRef'] && (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => clearColumnFilter('oloRef')} sx={{ p: 0.25 }}>
                                    <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Filter..."
                            value={columnFilters['externalReference'] || ''}
                            onChange={(e) => handleColumnFilterChange('externalReference', e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                              '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                              minWidth: 80,
                              width: '100%',
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                                </InputAdornment>
                              ),
                              endAdornment: columnFilters['externalReference'] && (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => clearColumnFilter('externalReference')} sx={{ p: 0.25 }}>
                                    <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['orderType'] || ''}
                        onChange={(e) => handleColumnFilterChange('orderType', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['orderType'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('orderType')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['deliveryPartner'] || ''}
                        onChange={(e) => handleColumnFilterChange('deliveryPartner', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['deliveryPartner'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('deliveryPartner')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['salesAmount'] || ''}
                        onChange={(e) => handleColumnFilterChange('salesAmount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['salesAmount'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('salesAmount')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['deliveryFee'] || ''}
                        onChange={(e) => handleColumnFilterChange('deliveryFee', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['deliveryFee'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('deliveryFee')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['taxAmount'] || ''}
                        onChange={(e) => handleColumnFilterChange('taxAmount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['taxAmount'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('taxAmount')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['tipAmount'] || ''}
                        onChange={(e) => handleColumnFilterChange('tipAmount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['tipAmount'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('tipAmount')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['totalAmount'] || ''}
                        onChange={(e) => handleColumnFilterChange('totalAmount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['totalAmount'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('totalAmount')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDetails.map((detail, index) => (
                    <TableRow
                      key={`${detail.orderGuid}-${index}`}
                      sx={{
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                      }}
                    >
                      {showAllOrderIds && (
                        <>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {detail.orderGuid || '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {detail.oloRef || '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {detail.externalReference || '-'}
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        <Chip
                          icon={detail.orderType === 'Delivery' ?
                            <LocalShippingIcon sx={{ fontSize: 14 }} /> :
                            <StorefrontIcon sx={{ fontSize: 14 }} />
                          }
                          label={detail.orderType || 'Unknown'}
                          size="small"
                          color={detail.orderType === 'Delivery' ? 'info' : 'secondary'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        {detail.deliveryPartner || '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(detail.salesAmount)}</TableCell>
                      <TableCell align="right" sx={{ color: parseFloat(detail.deliveryFee) > 0 ? 'info.main' : 'text.secondary' }}>
                        {formatCurrency(detail.deliveryFee)}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(detail.taxAmount)}</TableCell>
                      <TableCell align="right" sx={{ color: parseFloat(detail.tipAmount) > 0 ? 'success.main' : 'text.secondary' }}>
                        {formatCurrency(detail.tipAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 500 }}>
                        {formatCurrency(detail.totalAmount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredDetails.length} order{filteredDetails.length !== 1 ? 's' : ''}
                  {hasActiveFilters && ` (filtered from ${details.length})`}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showAllOrderIds}
                      onChange={(e) => setShowAllOrderIds(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show All Order IDs</Typography>}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2.5 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Sales
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatCurrency(totals.sales)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Delivery
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'info.main' }}>
                    {formatCurrency(totals.deliveryFee)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Tax
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatCurrency(totals.tax)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Tips
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                    {formatCurrency(totals.tip)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totals.total)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={details.length === 0}
        >
          Export
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default EzCaterDetailsDialog;
